/* ==========================================================================
 * DEAL — client-side deal display
 * --------------------------------------------------------------------------
 * Driven entirely by Theme settings → Deal. No dates.
 *
 * WHY CLIENT-SIDE: collection / search / recommendation grids are rendered by
 * NOSTO from its own copy of the theme card template, injected asynchronously.
 * Server Liquid never touches those, so the deal display is applied in the
 * browser and re-applied whenever Nosto (re)injects (MutationObserver).
 *
 * NO DATE GATE. There is no schedule and no "today". The deal is whatever the
 * server emitted from the theme settings: if it is switched off, pct is 0 and
 * this file does nothing at all.
 *
 * MATCHING: a card qualifies on EITHER signal —
 *   • BRAND   — by vendor, whole-word + diacritic-insensitive (so "rms" matches
 *               "RMS Beauty" but not a stray substring, and "roz" matches "RŌZ").
 *   • COLLECTION — by the card's own collection-for attribute, which carries
 *               handles on theme cards and TITLES on Nosto cards.
 * Both are per-card, so a collection-only deal still displays, and a
 * recommendation carousel of other brands on the same page stays untouched.
 * Cards with NO collection data (Nosto search results and the autocomplete
 * dropdown) ask the server instead — see serverPct().
 * Skips genuine markdowns (struck > shown), sold-out, gift cards, $0,
 * subscriptions, and anything the server already rendered (data-dd-server).
 *
 * ⚠️  DISPLAY ONLY — pair with a matching Shopify AUTOMATIC DISCOUNT.
 * ======================================================================== */
(function () {
  'use strict';

  var STYLE_ID = 'dd-style';
  var APPLIED = '1';   // idempotency token (there is no date to key on)
  // Styles for inside the Skio shadow root. The struck price there is a <span>,
  // not an <s>, so the line-through has to be explicit.
  var SKIO_CSS =
    '.dd-was{opacity:.55;font-weight:400;margin-right:.35em;text-decoration:line-through}' +
    '.dd-now{color:#c0392b;font-weight:600}';
  var AC_TILE = '.ns-ac-prod';  // Nosto Autocomplete product tile (header search dropdown)
  var applying = false;
  var timer = null;
  var deal = null;
  var dayCollections = [];      // handles, normalized
  var dayCollectionTitles = []; // titles, normalized
  var skioObserved = false;
  var skioTimer = null;
  var LOOKUP_KEY = 'dd-lookup';
  var lookups = {};             // handle → server deal pct (0 = not on deal)
  var pending = {};             // handle → true while its lookup is in flight

  function boot() {
    var scriptEl = document.getElementById('DailyDealSchedule');
    if (!scriptEl) return;

    try { deal = JSON.parse(scriptEl.textContent); }
    catch (e) { console.warn('[daily-deal] bad deal JSON', e); return; }

    // Deal switched off in the theme settings → do nothing, attach no observer.
    if (!deal || !(deal.pct > 0)) { deal = null; return; }

    // Pre-normalize the collection spellings once.
    dayCollections = (deal.collections || []).map(normalize).filter(Boolean);
    dayCollectionTitles = (deal.collectionTitles || []).map(normalize).filter(Boolean);

    loadLookups();
    injectStyle();
    applyAll();

    // Nosto injects/replaces cards after load and on sort/filter/paging.
    // Only react to mutations that actually add price nodes.
    var obs = new MutationObserver(function (muts) {
      if (applying) return;
      if (!hasPriceNode(muts)) return;
      scheduleApply();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  function hasPriceNode(muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && (n.matches('.price') || n.matches(AC_TILE))) return true;
        if (n.querySelector && (n.querySelector('.price') || n.querySelector(AC_TILE))) return true;
      }
    }
    return false;
  }

  function scheduleApply() {
    clearTimeout(timer);
    timer = setTimeout(applyAll, 150);
  }

  // Server answer for one product: the deal pct, 0 when it is not on deal, or
  // undefined while the lookup is still in flight (the card is re-applied when
  // it lands). templates/product.dd.liquid runs the same daily-deal snippet as
  // the PDP, so brand, collection, exclusion and gift-card rules all match.
  function serverPct(handle) {
    if (!handle) return 0;
    if (lookups.hasOwnProperty(handle)) return lookups[handle];
    if (pending[handle]) return undefined;
    pending[handle] = true;

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'products/' + encodeURIComponent(handle) + '?view=dd')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (text) {
        lookups[handle] = parseInt(text, 10) || 0;
        delete pending[handle];
        saveLookups();
        scheduleApply();
      })
      .catch(function (e) {
        console.warn('[daily-deal] lookup failed', handle, e);
        lookups[handle] = 0;
        delete pending[handle];
      });
    return undefined;
  }

  // Cache lookups for the session, keyed on the deal so a settings change in
  // the theme editor starts fresh.
  function loadLookups() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(LOOKUP_KEY) || 'null');
      if (saved && saved.deal === JSON.stringify(deal)) lookups = saved.lookups || {};
    } catch (e) { /* storage unavailable: look up again */ }
  }

  function saveLookups() {
    try {
      sessionStorage.setItem(LOOKUP_KEY, JSON.stringify({ deal: JSON.stringify(deal), lookups: lookups }));
    } catch (e) { /* storage unavailable: lookups stay in memory */ }
  }

  function applyAll() {
    if (!deal) return;
    applying = true;
    try {
      var factor = (100 - deal.pct) / 100;
      var prices = document.querySelectorAll('.price');
      for (var i = 0; i < prices.length; i++) applyToPrice(prices[i], factor);
      var tiles = document.querySelectorAll(AC_TILE);
      for (var j = 0; j < tiles.length; j++) applyToAutocompleteTile(tiles[j], factor);
      applyToSkio(factor);
    } finally {
      applying = false;
    }
  }

  // Skio's plan picker renders inside a SHADOW ROOT, so querySelectorAll('.price')
  // above cannot see it and the one-time line kept quoting the full price while
  // the PDP hero right above it showed the deal price.
  //
  // Matching is not re-derived here. We act only when the SERVER has already
  // marked this PDP as on-deal (data-dd-server), so the widget can never claim a
  // discount the rest of the page does not. Only the ONE-TIME price is touched;
  // the subscription line has its own separate discount and is left alone.
  function applyToSkio(factor) {
    var picker = document.querySelector('skio-plan-picker');
    if (!picker || !picker.shadowRoot) return;
    if (!document.querySelector('product-info .price[data-dd-server]')) return;

    var priceEl = picker.shadowRoot.querySelector('[skio-onetime-price]');
    if (!priceEl) return;

    // NEVER write inside priceEl. Skio is a Lit component and that span holds a
    // dynamic part:  <span skio-onetime-price>${...}</span>. Replacing its
    // children destroys the marker nodes Lit needs to patch it, after which Lit
    // silently stops updating it — which froze the one-time price at whichever
    // variant was selected when we first ran. Instead: READ it (Lit keeps it
    // correct for the selected variant), strike it via a class on the span
    // itself (a static attribute Lit does not manage), and render the deal price
    // into a sibling node that belongs to us.
    var original = priceEl.textContent.replace(/\s+/g, ' ').trim();
    if (!(firstNumber(original) > 0)) return;

    priceEl.classList.add('dd-was');

    var host = priceEl.parentNode;
    if (!host) return;
    var nowEl = host.querySelector('.dd-now');
    if (!nowEl) {
      nowEl = document.createElement('span');
      nowEl.className = 'dd-now';
      host.appendChild(nowEl);
    }

    // Write only on change: our own mutation would otherwise re-trigger the
    // observer below and loop.
    var dealt = scaleMoney(original, factor);
    if (nowEl.textContent !== dealt) nowEl.textContent = dealt;

    // Shadow DOM does not inherit the page stylesheet, so dd-was / dd-now have
    // to be styled inside the shadow root too.
    if (!picker.shadowRoot.getElementById(STYLE_ID)) {
      var s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = SKIO_CSS;
      picker.shadowRoot.appendChild(s);
    }

    if (!skioObserved) {
      skioObserved = true;
      new MutationObserver(function () {
        if (applying) return;
        clearTimeout(skioTimer);
        skioTimer = setTimeout(applyAll, 120);
      }).observe(picker.shadowRoot, { childList: true, subtree: true, characterData: true });
    }
  }

  // The header search dropdown is Nosto Autocomplete, which shares NOTHING with
  // the card markup above — no .price, no price-for, no .card__badge. Its tiles
  // are .ns-ac-prod with a .ns-ac-prod-brand label and a bare <span
  // class="final-price">$39.00</span>. Without this pass the dropdown quotes the
  // undiscounted price while the PLP and PDP behind it show the deal.
  function applyToAutocompleteTile(tile, factor) {
    if (tile.getAttribute('data-dd-applied') === APPLIED) return; // idempotent

    var wrap = tile.querySelector('.ns-ac-prod-price-wrap');
    if (!wrap) return;
    var finalEl = wrap.querySelector('.final-price');
    if (!finalEl || finalEl.querySelector('.dd-was')) return;

    // A genuine markdown already shows two money nodes (was + now) — leave it.
    if (wrap.querySelectorAll('*').length > 1) return;

    var url = tile.getAttribute('data-url') || '';
    var handle = (url.split('/products/')[1] || '').split(/[?#]/)[0].toLowerCase();
    if (handle.indexOf('gift-card') !== -1 || handle === 'giftcard') return;

    var brandEl = tile.querySelector('.ns-ac-prod-brand');
    var vendor = brandEl ? brandEl.textContent : '';

    // These tiles carry no collection context, only a bare product URL, so a
    // collection deal is resolved by asking the server.
    if (!vendorMatches(vendor, deal.brands) && !(serverPct(handle) > 0)) return;
    if (anyContains(handle, deal.exclude)) return;

    var original = finalEl.textContent.replace(/\s+/g, ' ').trim();
    if (!(firstNumber(original) > 0)) return;

    finalEl.innerHTML =
      '<s class="dd-was">' + escapeHtml(original) + '</s> ' +
      '<span class="dd-now">' + escapeHtml(scaleMoney(original, factor)) + '</span>';
    tile.setAttribute('data-dd-applied', APPLIED);
  }

  function applyToPrice(priceEl, factor) {
    if (priceEl.hasAttribute('data-dd-server')) return;               // server already rendered it
    if (priceEl.getAttribute('data-dd-applied') === APPLIED) return; // idempotent
    if (priceEl.closest('[class*="skio"], skio-plan-picker')) return; // subscription widget

    // Sold-out cards are deliberately NOT skipped. The server now deal-prices
    // sold-out variants (see price.liquid — the Google feed discounts them, so
    // the page has to as well), and skipping them here would leave the Nosto
    // grids showing full price while the PDP shows the deal price.
    var card = priceEl.closest(
      '.card-wrapper, .product-card-wrapper, product-card, .card, li.grid__item, .grid__item'
    );

    var reg = priceEl.querySelector('.price__regular .price-item--regular') ||
              priceEl.querySelector('.price-item--regular[price-for]');
    if (!reg) return;

    var handle = (reg.getAttribute('price-for') || '').toLowerCase();
    if (handle.indexOf('gift-card') !== -1) return;                  // never discount gift cards

    var original = reg.textContent.replace(/\s+/g, ' ').trim();
    var regNum = firstNumber(original);
    if (!(regNum > 0)) return;                                       // $0 / unparseable

    // Already-marked-down items are deliberately NOT skipped. The automatic
    // discount stacks on the current price regardless of compare_at, and the
    // server now deal-prices them too (see price.liquid), so skipping here
    // would leave the Nosto grids disagreeing with the PDP and the feed.

    // vendor: theme attr first, else the card's visible vendor label (Nosto cards)
    var vendor = priceEl.getAttribute('data-dd-vendor') || '';
    if (!vendor && card) {
      var vEl = card.querySelector('.newVendor, [class*="vendor"]');
      if (vEl) vendor = vEl.textContent;
    }

    var match = vendorMatches(vendor, deal.brands) || collectionMatches(reg, card);
    // Every theme renderer emits collection-for (even empty); only Nosto's
    // search cards omit it, so for those the browser can't tell — ask.
    if (!match && !reg.hasAttribute('collection-for') && !reg.hasAttribute('data-dd-collections')) {
      match = serverPct(handle) > 0;
    }
    if (match && anyContains(handle, deal.exclude)) match = false;
    if (!match) return;

    var dealt = scaleMoney(original, factor); // handles ranges ($115.00 - $130.00)

    reg.innerHTML =
      '<span class="visually-hidden">Regular price </span>' +
      '<s class="dd-was">' + escapeHtml(original) + '</s> ' +
      '<span class="visually-hidden">Sale price </span>' +
      '<span class="dd-now">' + escapeHtml(dealt) + '</span>';
    priceEl.setAttribute('data-dd-applied', APPLIED);

    addBadge(card, deal.pct);
  }

  function addBadge(card, pct) {
    if (!card) return;
    var holder = card.querySelector('.card__badge');
    if (!holder) return;
    if (holder.querySelector('.dd-badge')) return;   // already added
    if (holder.textContent.trim() !== '') return;    // don't stomp a real badge
    var span = document.createElement('span');
    span.className = 'dd-badge badge badge--bottom-left';
    span.textContent = pct + '% OFF';
    holder.appendChild(span);
  }

  // Scale EVERY number in a money string (handles ranges), rounding to cents and
  // matching the theme's "$91" / "$80.50" style (drop trailing .00).
  function scaleMoney(str, factor) {
    return str.replace(/\d[\d.,]*/g, function (m) {
      var n = parseFloat(m.replace(/,/g, ''));
      if (isNaN(n)) return m;
      var cents = Math.round(n * factor * 100);
      // Match the source's decimal style: if it showed decimals, keep 2 (so a
      // range stays consistent); if it was whole, drop .00 but keep odd cents.
      var hadDecimals = /[.,]\d{2}$/.test(m);
      var minFrac = hadDecimals ? 2 : (cents % 100 === 0 ? 0 : 2);
      return (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: minFrac,
        maximumFractionDigits: 2
      });
    });
  }

  function firstNumber(str) {
    var m = (str || '').replace(/,/g, '').match(/\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
  }

  // lowercase, strip diacritics (RŌZ → roz), collapse whitespace
  function normalize(s) {
    return (s || '').toLowerCase().normalize('NFD')
      .replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
  }

  // whole-word match: token must appear bounded by spaces, not as a substring
  function vendorMatches(vendor, brands) {
    if (!brands) return false;
    var padded = ' ' + normalize(vendor) + ' ';
    for (var i = 0; i < brands.length; i++) {
      var t = normalize(brands[i]);
      if (t && padded.indexOf(' ' + t + ' ') !== -1) return true;
    }
    return false;
  }

  // Does this card belong to one of the day's collections?
  //
  // Per-card only, deliberately: we do NOT treat "the shopper is on a deal
  // collection page" as a blanket match, because recommendation carousels of
  // other brands render on that same page and would get a fake discount. For
  // the same reason we do NOT infer membership from a /collections/<x>/products
  // URL — Shopify scopes those links to the page the shopper came from, so that
  // is page context, not product data.
  //
  // collection-for carries a LIST, and the two card families spell it
  // differently (verified in the live DOM):
  //   theme cards — "alpyn-beauty, vegan, skin-care"      (handles, bare)
  //   Nosto cards — "[Alpyn Beauty, Vegan Beauty, Skin care]" (titles, bracketed)
  // So: strip brackets, split on commas, and test every entry against BOTH the
  // day's handles and its titles. Comparing the whole attribute against a single
  // title (the previous behaviour) could never match either family.
  function collectionMatches(reg, card) {
    if (!dayCollections.length && !dayCollectionTitles.length) return false;

    var raw = (reg.getAttribute('collection-for') || '') + ',' +
              (reg.getAttribute('data-dd-collections') || '');
    var entries = raw.replace(/^\s*\[|\]\s*$/g, '').split(',');

    for (var i = 0; i < entries.length; i++) {
      var e = normalize(entries[i].replace(/^\s*\[|\]\s*$/g, ''));
      if (!e) continue;
      if (dayCollections.indexOf(e) !== -1) return true;
      if (dayCollectionTitles.indexOf(e) !== -1) return true;
    }

    return false;
  }

  // substring match — correct for handle exclusions (e.g. "everything-soap-lemongrass")
  function anyContains(haystack, needles) {
    if (!haystack || !needles) return false;
    var h = haystack.toLowerCase();
    for (var i = 0; i < needles.length; i++) {
      var n = (needles[i] || '').toLowerCase();
      if (n && h.indexOf(n) !== -1) return true;
    }
    return false;
  }

  function escapeHtml(s) {
    return s.replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.dd-was{opacity:.55;font-weight:400;margin-right:.35em}' +
      '.dd-now{color:#c0392b;font-weight:600}' +
      '.dd-badge{background:#023e55;color:#fff}';
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = css;
    document.head.appendChild(s);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
